import React, { useState, useEffect } from 'react';
import './AdminView.css';
import ClientList from './ClientList';
import ClientDetails from './ClientDetails';
import PropertyDetails from './PropertyDetails';
import ClientModal from './ClientModal';
import PropertyModal from './PropertyModal';

type ViewState = {
  view: 'client-list' | 'client-details' | 'property-details';
  clientId?: number;
  propertyId?: number;
};

interface Client {
  id?: number;
  client_name: string;
  jump_client_id?: string;
  client_acronym?: string;
  client_type?: string;
  client_website?: string;
  active: boolean;
  addresses?: Array<{
    address_type: string;
    address_line1: string;
    address_line2?: string;
    city: string;
    state: string;
    zip_code: string;
  }>;
  contacts?: Array<{
    contact_type: string;
    contact_title?: string;
    contact_name: string;
    contact_email?: string;
    contact_phone?: string;
  }>;
}

interface AdminViewProps {
  resetTrigger?: number;
}

const AdminView: React.FC<AdminViewProps> = ({ resetTrigger }) => {
  const [viewState, setViewState] = useState<ViewState>({ view: 'client-list' });
  const [clientModal, setClientModal] = useState<{
    isOpen: boolean;
    mode: 'add' | 'edit';
    clientId?: number;
  }>({
    isOpen: false,
    mode: 'add',
    clientId: 0
  });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [propertyRefreshTrigger, setPropertyRefreshTrigger] = useState(0);
  const [propertyModal, setPropertyModal] = useState<{
    isOpen: boolean;
    mode: 'add' | 'edit';
    propertyId?: number;
    clientId?: number;
  }>({
    isOpen: false,
    mode: 'add',
    propertyId: 0,
    clientId: 0
  });

  // Reset to client list when Admin view is loaded
  useEffect(() => {
    setViewState({ view: 'client-list' });
  }, []);

  // Reset to client list when resetTrigger changes
  useEffect(() => {
    if (resetTrigger !== undefined) {
      setViewState({ view: 'client-list' });
    }
  }, [resetTrigger]);

  const handleClientSelect = (clientId: number) => {
    setViewState({ view: 'client-details', clientId });
  };

  const handleBackToClientList = () => {
    setViewState({ view: 'client-list' });
  };

  const handleBackToClientDetails = () => {
    if (viewState.clientId) {
      setViewState({ view: 'client-details', clientId: viewState.clientId });
    } else {
      setViewState({ view: 'client-list' });
    }
  };

  const handleShowPropertyDetails = (clientId: number, propertyId: number) => {
    setViewState({ view: 'property-details', clientId, propertyId });
  };

  const handleAddClient = () => {
    setClientModal({
      isOpen: true,
      mode: 'add',
      clientId: 0
    });
  };

  const handleEditClient = (client: Client) => {
    setClientModal({
      isOpen: true,
      mode: 'edit',
      clientId: client.id
    });
  };

  const handleCloseClientModal = () => {
    setClientModal({
      isOpen: false,
      mode: 'add',
      clientId: 0
    });
  };

  const handleClientSuccess = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const handleAddProperty = (clientId: number) => {
    setPropertyModal({
      isOpen: true,
      mode: 'add',
      propertyId: 0,
      clientId
    });
  };

  const handleEditProperty = (clientId: number, propertyId: number) => {
    setPropertyModal({
      isOpen: true,
      mode: 'edit',
      propertyId,
      clientId
    });
  };

  const handleClosePropertyModal = () => {
    setPropertyModal({
      isOpen: false,
      mode: 'add',
      propertyId: 0,
      clientId: 0
    });
  };

  const handlePropertySuccess = () => {
    setPropertyRefreshTrigger(prev => prev + 1);
  };


  const renderView = () => {
    switch (viewState.view) {
      case 'client-list':
        return (
          <ClientList
            onClientSelect={handleClientSelect}
            onAddClient={handleAddClient}
          />
        );

      case 'client-details':
        return (
          <ClientDetails
            clientId={viewState.clientId!}
            onBack={handleBackToClientList}
            onEditClient={handleEditClient}
            onShowPropertyDetails={handleShowPropertyDetails}
            onAddProperty={handleAddProperty}
            onEditProperty={handleEditProperty}
            refreshTrigger={refreshTrigger}
            propertyRefreshTrigger={propertyRefreshTrigger}
          />
        );

      case 'property-details':
        return (
          <PropertyDetails
            clientId={viewState.clientId!}
            propertyId={viewState.propertyId!}
            onBack={handleBackToClientDetails}
          />
        );


      default:
        return (
          <ClientList
            onClientSelect={handleClientSelect}
            onAddClient={handleAddClient}
          />
        );
    }
  };

  return (
    <div className="admin-view">
      {renderView()}

      <ClientModal
        isOpen={clientModal.isOpen}
        onClose={handleCloseClientModal}
        clientId={clientModal.clientId}
        mode={clientModal.mode}
        onSuccess={handleClientSuccess}
      />

      <PropertyModal
        isOpen={propertyModal.isOpen}
        onClose={handleClosePropertyModal}
        propertyId={propertyModal.propertyId}
        clientId={propertyModal.clientId || 0}
        mode={propertyModal.mode}
        onSuccess={handlePropertySuccess}
      />
    </div>
  );
};

export default AdminView;